import { useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../utils/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Heart } from 'lucide-react';

const NPSRespond = () => {
    const { token } = useParams();
    const [score, setScore] = useState(null);
    const [feedback, setFeedback] = useState('');
    const [submitted, setSubmitted] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (score === null) return;
        setBusy(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/nps/respond/${token}`, { score, feedback });
            setSubmitted(data);
        } catch (e) {
            setError(e.response?.data?.detail || 'Submission failed');
        } finally {
            setBusy(false);
        }
    };

    const colorFor = (n) => n >= 9 ? 'bg-emerald-500' : n >= 7 ? 'bg-amber-400' : 'bg-red-500';

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50 flex items-center justify-center p-4" data-testid="nps-respond-page">
            <Card className="rounded-2xl shadow-lg border border-slate-200 max-w-2xl w-full overflow-hidden">
                <CardContent className="p-8 sm:p-12">
                    <div className="flex items-center gap-3 mb-6">
                        <img src="/digi-loans-logo.png" alt="DIGI-LOANS" className="h-12 w-12 object-contain" />
                        <div>
                            <h1 className="text-xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>DIGI-LOANS</h1>
                            <p className="text-[10px] text-slate-500 tracking-wider uppercase">Your feedback matters</p>
                        </div>
                    </div>

                    {submitted ? (
                        <div className="text-center py-8" data-testid="nps-success">
                            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-pink-100 mb-4">
                                <Heart className="h-10 w-10 text-pink-500" />
                            </div>
                            <h2 className="text-2xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>{submitted.message}</h2>
                            <p className="text-slate-500 text-sm">We've shared your feedback with the team. {submitted.bucket === 'detractor' ? "We're sorry we didn't meet expectations — our team will reach out." : ""}</p>
                        </div>
                    ) : error ? (
                        <div className="text-center py-8">
                            <h2 className="text-2xl font-bold text-red-600 mb-2">Oops</h2>
                            <p className="text-slate-500 text-sm">{error}</p>
                        </div>
                    ) : (
                        <>
                            <h2 className="text-2xl sm:text-3xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>How likely are you to recommend DIGI-LOANS?</h2>
                            <p className="text-slate-500 text-sm mb-8">0 = not at all likely · 10 = extremely likely</p>

                            <div className="grid grid-cols-11 gap-1.5 mb-8" data-testid="nps-scale">
                                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                                    <button
                                        key={n}
                                        onClick={() => setScore(n)}
                                        className={`h-12 rounded-lg font-bold text-base transition-all ${score === n ? `${colorFor(n)} text-white scale-110 shadow-lg` : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                                        data-testid={`nps-score-${n}`}
                                    >
                                        {n}
                                    </button>
                                ))}
                            </div>

                            {score !== null && (
                                <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2">
                                    <div>
                                        <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Tell us more (optional)</label>
                                        <Textarea
                                            value={feedback}
                                            onChange={e => setFeedback(e.target.value)}
                                            rows={3}
                                            className="rounded-lg mt-1 bg-slate-50 border-slate-200"
                                            placeholder={score >= 9 ? "What did we do really well?" : score >= 7 ? "What can we improve?" : "Sorry to hear that — how can we make this right?"}
                                            data-testid="nps-feedback"
                                        />
                                    </div>
                                    <Button onClick={submit} disabled={busy} className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg shadow-sm h-12 font-bold" data-testid="nps-submit">
                                        {busy ? 'Sending...' : 'Submit feedback'}
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default NPSRespond;
